import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, XCircle, Clock, Upload, ChevronDown, ChevronUp,
  FileText, Loader2, AlertCircle, Search, RefreshCw, User,
  SlidersHorizontal, X, ZoomIn, ZoomOut, Download, ExternalLink
} from 'lucide-react';
import { paymentsAPI } from '../services/api';
import { useLocale } from '../contexts/LocaleContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getProofURL = (filePath) => {
  const base = (import.meta.env.VITE_API_URL || '').replace(/\/api$/, '');
  return `${base}/${filePath}`;
};

const isImage = (filePath) =>
  /\.(jpg|jpeg|png|gif|webp)$/i.test(filePath);

// ─── Mobile detection ─────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

// ─── Proof Viewer Modal ───────────────────────────────────────────────────────
function ProofModal({ filePath, onClose, t }) {
  const [zoom, setZoom] = useState(1);
  const url = getProofURL(filePath);
  const isImg = isImage(filePath);

  useEffect(() => {
    const h = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ backgroundColor: 'rgba(0,0,0,0.92)' }}
    >
      <div
        className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0"
        style={{ borderColor: 'rgba(255,255,255,0.1)', backgroundColor: 'rgba(0,0,0,0.6)' }}
      >
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.5)' }} />
          <span className="text-xs font-mono truncate max-w-[200px] sm:max-w-md" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {filePath.split('/').pop()}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {isImg && (
            <>
              <button
                onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}
                className="p-1.5 rounded-lg transition-colors"
                style={{ color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.08)' }}
                title={t('deposits.zoomOut')}
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-xs font-mono w-10 text-center" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={() => setZoom(z => Math.min(3, z + 0.25))}
                className="p-1.5 rounded-lg transition-colors"
                style={{ color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.08)' }}
                title={t('deposits.zoomIn')}
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </>
          )}

          <a
            href={url}
            download
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.08)' }}
            title={t('common.download')}
          >
            <Download className="w-4 h-4" />
          </a>

          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.08)' }}
            title={t('deposits.openInNewTab')}
          >
            <ExternalLink className="w-4 h-4" />
          </a>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors ml-1"
            style={{ color: 'rgba(255,255,255,0.9)', backgroundColor: 'rgba(255,255,255,0.12)' }}
            title={t('common.close')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        className="flex-1 overflow-auto flex items-center justify-center p-4"
        onClick={onClose}
      >
        {isImg ? (
          <img
            src={url}
            alt={t('deposits.proofThumbnail')}
            onClick={e => e.stopPropagation()}
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'center',
              transition: 'transform 0.15s ease',
              maxWidth: '100%',
              borderRadius: '8px',
              boxShadow: '0 25px 80px rgba(0,0,0,0.6)',
            }}
          />
        ) : (
          <div
            className="w-full h-full"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '900px', minHeight: '500px' }}
          >
            <iframe
              src={url}
              title={t('deposits.depositProofPdf')}
              className="w-full rounded-lg"
              style={{ height: 'calc(100vh - 120px)', border: 'none' }}
            />
          </div>
        )}
      </div>

      <div className="text-center pb-3 flex-shrink-0">
        <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.25)' }}>
          {t('common.close')}
        </p>
      </div>
    </div>
  );
}

// ─── Proof trigger button ─────────────────────────────────────────────────────
function ProofButton({ filePath, onOpen, mobile = false, t }) {
  if (!filePath) return null;
  if (mobile) {
    return (
      <button
        onClick={() => onOpen(filePath)}
        className="flex items-center gap-1.5 text-xs font-semibold"
        style={{ color: 'var(--accent-primary)' }}
      >
        <FileText className="w-3.5 h-3.5" />{t('deposits.depositProof')}
      </button>
    );
  }
  return (
    <button
      onClick={() => onOpen(filePath)}
      className="inline-flex items-center gap-1.5 text-xs hover:underline transition-smooth"
      style={{ color: 'var(--accent-primary)' }}
    >
      <FileText className="w-3.5 h-3.5" />{t('deposits.depositProof')}
    </button>
  );
}

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  pending:        { bg: 'bg-yellow-100 dark:bg-yellow-900/30',  text: 'text-yellow-700 dark:text-yellow-400',  icon: Clock,       varBg: 'var(--accent-warning)', varColor: 'var(--accent-warning)' },
  proof_uploaded: { bg: 'bg-blue-100 dark:bg-blue-900/30',     text: 'text-blue-700 dark:text-blue-400',      icon: Upload,      varBg: 'var(--accent-info)',    varColor: 'var(--accent-info)'    },
  approved:       { bg: 'bg-green-100 dark:bg-green-900/30',   text: 'text-green-700 dark:text-green-400',    icon: CheckCircle, varBg: 'var(--accent-success)', varColor: 'var(--accent-success)' },
  rejected:       { bg: 'bg-red-100 dark:bg-red-900/30',       text: 'text-red-700 dark:text-red-400',        icon: XCircle,     varBg: 'var(--accent-danger)',  varColor: 'var(--accent-danger)'  },
};

function StatusBadge({ status, t }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = s.icon;
  const label = t(`deposits.${status}`) || s.label;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      <Icon className="w-3.5 h-3.5" />{label}
    </span>
  );
}

function MobileStatusBadge({ status, t }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = s.icon;
  const label = t(`deposits.${status}`) || s.label;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
      style={{
        backgroundColor: `color-mix(in srgb, ${s.varBg} 15%, transparent)`,
        color: s.varColor,
        border: `1px solid color-mix(in srgb, ${s.varBg} 30%, transparent)`,
      }}
    >
      <Icon className="w-2.5 h-2.5" />{label}
    </span>
  );
}

// ─── Filter tabs ──────────────────────────────────────────────────────────────
const TABS = [
  { key: 'all',            label: 'allDeposits' },
  { key: 'proof_uploaded', label: 'pending' },
  { key: 'pending',        label: 'pending' },
  { key: 'approved',       label: 'approved' },
  { key: 'rejected',       label: 'rejected' },
];

// ─── Mobile deposit card ──────────────────────────────────────────────────────
function DepositCard({ d, expanded, onToggle, actionId, actionType, note, setNote, onOpenAction, onCancelAction, onSubmitAction, submitting, onOpenProof, t }) {
  const isOpen   = expanded === d.id;
  const isAction = actionId === d.id;
  const canAct   = d.status === 'proof_uploaded';

  return (
    <div
      className="rounded-2xl border overflow-hidden"
      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}
    >
      <button className="w-full flex items-center gap-3 px-4 py-3.5 text-left" onClick={() => onToggle(d.id)}>
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0"
          style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)' }}
        >
          <User className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>
            {d.trader_name || '—'}
          </p>
          <p className="text-[11px] truncate capitalize" style={{ color: 'var(--text-tertiary)' }}>
            {d.pluginKey?.replace('_', ' ')} · {d.trader_email || d.account_id}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <p className="text-sm font-black font-mono" style={{ color: 'var(--accent-success)' }}>
            +{Number(d.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
          <ChevronDown
            className="w-4 h-4 transition-transform duration-200"
            style={{ color: 'var(--text-tertiary)', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </div>
      </button>

      <div className="flex items-center gap-2 px-4 pb-3">
        <MobileStatusBadge status={d.status} t={t} />
        <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>{d.currency}</span>
        {canAct && (
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full ml-auto"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--accent-warning) 15%, transparent)',
              color: 'var(--accent-warning)',
            }}
          >
            {t('common.pending')}
          </span>
        )}
      </div>

      {isOpen && (
        <div
          className="border-t px-4 py-3 space-y-3"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'color-mix(in srgb, var(--bg-tertiary) 40%, transparent)' }}
        >
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('common.name')}</p>
              <p className="text-xs font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>#{d.id}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('common.date')}</p>
              <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                {new Date(d.created_at).toLocaleDateString()}
              </p>
            </div>
            {d.method_details?.bankName && (
              <div className="col-span-2">
                <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('deposits.bank')}</p>
                <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{d.method_details.bankName}</p>
              </div>
            )}
            {d.method_details?.coin && (
              <div>
                <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('deposits.crypto')}</p>
                <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {d.method_details.coin} / {d.method_details.network}
                </p>
              </div>
            )}
            {d.rejection_note && (
              <div className="col-span-2">
                <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--accent-danger)' }}>{t('deposits.rejectionReason')}</p>
                <p className="text-xs italic" style={{ color: 'var(--accent-danger)' }}>{d.rejection_note}</p>
              </div>
            )}
          </div>

          <ProofButton filePath={d.proof_file_path} onOpen={onOpenProof} mobile t={t} />

          {canAct && !isAction && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => onOpenAction(d.id, 'approve')}
                className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all active:scale-95"
                style={{
                  backgroundColor: 'color-mix(in srgb, var(--accent-success) 12%, transparent)',
                  color: 'var(--accent-success)',
                  border: '1px solid color-mix(in srgb, var(--accent-success) 25%, transparent)',
                }}
              >
                <CheckCircle className="w-3.5 h-3.5" /> {t('deposits.approve')}
              </button>
              <button
                onClick={() => onOpenAction(d.id, 'reject')}
                className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all active:scale-95"
                style={{
                  backgroundColor: 'color-mix(in srgb, var(--accent-danger) 12%, transparent)',
                  color: 'var(--accent-danger)',
                  border: '1px solid color-mix(in srgb, var(--accent-danger) 25%, transparent)',
                }}
              >
                <XCircle className="w-3.5 h-3.5" /> {t('deposits.reject')}
              </button>
            </div>
          )}

          {isAction && (
            <div
              className="rounded-xl p-3 space-y-2"
              style={{
                backgroundColor: actionType === 'approve'
                  ? 'color-mix(in srgb, var(--accent-success) 8%, transparent)'
                  : 'color-mix(in srgb, var(--accent-danger) 8%, transparent)',
                border: `1px solid color-mix(in srgb, ${actionType === 'approve' ? 'var(--accent-success)' : 'var(--accent-danger)'} 20%, transparent)`,
              }}
            >
              <p className="text-xs font-semibold" style={{ color: actionType === 'approve' ? 'var(--accent-success)' : 'var(--accent-danger)' }}>
                {actionType === 'approve' ? t('deposits.confirmApproval') : t('deposits.confirmRejection')}
              </p>
              {actionType === 'reject' && (
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder={t('deposits.rejectionReason')}
                  rows={2}
                  className="w-full px-3 py-2 rounded-xl border focus:outline-none text-xs"
                  style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                />
              )}
              <div className="flex gap-2">
                <button
                  onClick={onSubmitAction}
                  disabled={submitting}
                  className="flex-1 py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50 transition-all active:scale-95"
                  style={{
                    backgroundColor: actionType === 'approve' ? 'var(--accent-success)' : 'var(--accent-danger)',
                    color: 'white',
                  }}
                >
                  {submitting
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : actionType === 'approve' ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                  {submitting ? t('common.processing') : actionType === 'approve' ? t('deposits.approve') : t('deposits.reject')}
                </button>
                <button
                  onClick={onCancelAction}
                  className="px-4 py-2 rounded-xl text-xs font-bold transition-all active:scale-95"
                  style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Mobile filter drawer ─────────────────────────────────────────────────────
function FilterDrawer({ activeTab, setActiveTab, search, setSearch, counts, onClose, onClear, t }) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative rounded-t-2xl pt-4 pb-8 z-10 max-h-[80vh] overflow-y-auto"
        style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="w-10 h-1 rounded-full mx-auto mb-4" style={{ backgroundColor: 'var(--border-color)' }} />
        <div className="flex items-center justify-between px-4 mb-5">
          <h3 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{t('common.filter')}</h3>
          <button onClick={onClear} className="text-xs font-semibold flex items-center gap-1"
            style={{ color: 'var(--accent-primary)' }}>
            <X className="w-3.5 h-3.5" />{t('common.clearAll')}
          </button>
        </div>
        <div className="px-4 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-secondary)' }}>{t('common.search')}</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
              <input
                type="text" value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('deposits.idNameOrEmail')}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border focus:outline-none text-sm"
                style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--text-secondary)' }}>{t('common.status')}</label>
            <div className="space-y-2">
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm font-semibold transition-all"
                  style={{
                    backgroundColor: activeTab === tab.key ? 'color-mix(in srgb, var(--accent-primary) 12%, transparent)' : 'var(--bg-tertiary)',
                    borderColor: activeTab === tab.key ? 'color-mix(in srgb, var(--accent-primary) 30%, transparent)' : 'var(--border-color)',
                    color: activeTab === tab.key ? 'var(--accent-primary)' : 'var(--text-primary)',
                  }}
                >
                  {t(`deposits.${tab.label}`)}
                  <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{counts[tab.key]}</span>
                </button>
              ))}
            </div>
          </div>
          <button onClick={onClose}
            className="w-full py-3 rounded-xl text-sm font-bold text-white mt-1"
            style={{ backgroundColor: 'var(--accent-primary)' }}>
            {t('common.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function BrokerDepositsPage() {
  const { t } = useLocale();
  const isMobile = useIsMobile();

  const [deposits, setDeposits]     = useState([]);
  const [loading,  setLoading]      = useState(true);
  const [error,    setError]        = useState(null);
  const [activeTab, setActiveTab]   = useState('proof_uploaded');
  const [search,   setSearch]       = useState('');
  const [expanded, setExpanded]     = useState(null);
  const [actionId, setActionId]     = useState(null);
  const [actionType, setActionType] = useState(null);
  const [note,     setNote]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [proofFile, setProofFile]   = useState(null);

  const fetchDeposits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await paymentsAPI.getDeposits();
      setDeposits(data);
    } catch (e) {
      setError(e.message || t('deposits.failedToApprove'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchDeposits(); }, [fetchDeposits]);

  const filtered = deposits.filter((d) => {
    if (activeTab !== 'all' && d.status !== activeTab) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        String(d.id).includes(q) ||
        (d.trader_email || '').toLowerCase().includes(q) ||
        (d.trader_name  || '').toLowerCase().includes(q) ||
        (d.pluginKey    || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const openAction   = (id, type) => { setActionId(id); setActionType(type); setNote(''); };
  const cancelAction = () => { setActionId(null); setActionType(null); setNote(''); };

  const submitAction = async () => {
    setSubmitting(true);
    try {
      if (actionType === 'approve') await paymentsAPI.approveDeposit(actionId);
      else                           await paymentsAPI.rejectDeposit(actionId, { note });
      cancelAction();
      await fetchDeposits();
    } catch (e) {
      setError(e.message || t('errors.actionFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleExpanded = (id) => setExpanded(e => e === id ? null : id);

  const counts = {};
  TABS.forEach(tab => {
    counts[tab.key] = tab.key === 'all' ? deposits.length : deposits.filter(d => d.status === tab.key).length;
  });

  const needsReview = counts['proof_uploaded'] || 0;

  const openProof  = (filePath) => setProofFile(filePath);
  const closeProof = () => setProofFile(null);

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: 'var(--accent-primary)', borderTopColor: 'transparent' }} />
        </div>
      );
    }

    return (
      <div className="flex flex-col -mx-4 -my-6 min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>

        {proofFile && <ProofModal filePath={proofFile} onClose={closeProof} t={t} />}

        <div className="px-4 pt-5 pb-6" style={{
          background: 'linear-gradient(135deg, var(--accent-primary) 0%, color-mix(in srgb, var(--accent-primary) 60%, var(--accent-info)) 100%)'
        }}>
          <p className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {t('deposits.title')}
          </p>
          <p className="text-4xl font-black text-white leading-none font-mono">{counts.all}</p>
          <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>{t('dashboard.totalDeposits')}</p>
          <div className="grid grid-cols-3 gap-2 mt-4">
            {[
              { label: t('common.pending'),   value: needsReview },
              { label: t('deposits.approved'), value: counts.approved },
              { label: t('deposits.rejected'), value: counts.rejected },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-xl px-3 py-2.5 text-center"
                style={{ backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <p className="text-sm font-black text-white">{value}</p>
                <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 px-4 py-3 border-b"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
            <input
              type="text" value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('deposits.searchPlaceholder')}
              className="w-full pl-9 pr-3 py-2 rounded-xl border text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            />
          </div>
          <button
            onClick={() => setShowFilters(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold relative"
            style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          >
            <SlidersHorizontal className="w-4 h-4" />
            {t(`deposits.${TABS.find(tab => tab.key === activeTab)?.label}`)}
            {needsReview > 0 && activeTab === 'all' && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-black flex items-center justify-center text-white"
                style={{ backgroundColor: 'var(--accent-warning)' }}>
                {needsReview}
              </span>
            )}
          </button>
          <button onClick={fetchDeposits} className="p-2 rounded-xl border"
            style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {activeTab !== 'all' && (
          <div className="flex items-center gap-2 px-4 py-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <div className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)' }}>
              {t(`deposits.${TABS.find(tab => tab.key === activeTab)?.label}`)}
              <button onClick={() => setActiveTab('all')}><X className="w-2.5 h-2.5" /></button>
            </div>
          </div>
        )}

        {error && (
          <div className="mx-4 mt-3 flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 12%, transparent)', color: 'var(--accent-danger)', border: '1px solid color-mix(in srgb, var(--accent-danger) 25%, transparent)' }}>
            <AlertCircle className="w-4 h-4 shrink-0" />{error}
          </div>
        )}

        <div className="px-4 py-2 border-b" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            {filtered.length} {filtered.length !== 1 ? t('deposits.allDeposits') : t('deposits.title')}
          </p>
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <AlertCircle className="w-10 h-10 mb-3" style={{ color: 'var(--text-tertiary)' }} />
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('deposits.noDepositsFound')}</p>
            <button onClick={() => setActiveTab('all')} className="mt-2 text-xs font-semibold" style={{ color: 'var(--accent-primary)' }}>
              {t('common.all')}
            </button>
          </div>
        ) : (
          <div className="px-4 pt-3 pb-8 space-y-2">
            {filtered.map(d => (
              <DepositCard
                key={d.id}
                d={d}
                expanded={expanded}
                onToggle={toggleExpanded}
                actionId={actionId}
                actionType={actionType}
                note={note}
                setNote={setNote}
                onOpenAction={openAction}
                onCancelAction={cancelAction}
                onSubmitAction={submitAction}
                submitting={submitting}
                onOpenProof={openProof}
                t={t}
              />
            ))}
          </div>
        )}

        {showFilters && (
          <FilterDrawer
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            search={search}
            setSearch={setSearch}
            counts={counts}
            onClose={() => setShowFilters(false)}
            onClear={() => { setActiveTab('all'); setSearch(''); }}
            t={t}
          />
        )}

        <div className="pb-[env(safe-area-inset-bottom)]" />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP
  // ══════════════════════════════════════════════════════════════════════════
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--accent-primary)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto">

      {proofFile && <ProofModal filePath={proofFile} onClose={closeProof} t={t} />}

      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-[var(--text-primary)]">{t('deposits.title')}</h1>
        <button onClick={fetchDeposits} className="px-4 py-2 border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-secondary)] text-sm flex items-center gap-1.5 text-[var(--text-primary)] transition-smooth">
          <RefreshCw className="w-3.5 h-3.5" /> {t('common.refresh')}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: t('common.total'),        value: counts.all,            color: 'text-[var(--text-primary)]' },
          { label: t('common.pending'), value: counts.proof_uploaded, color: 'text-blue-600 dark:text-blue-400' },
          { label: t('deposits.approved'),     value: counts.approved,       color: 'text-green-600 dark:text-green-400' },
          { label: t('deposits.rejected'),     value: counts.rejected,       color: 'text-red-600 dark:text-red-400' },
        ].map(c => (
          <div key={c.label} className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-4">
            <p className="text-xs text-[var(--text-secondary)]">{c.label}</p>
            <p className={`text-2xl font-bold mt-0.5 ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-smooth ${activeTab === tab.key ? 'bg-[var(--accent-primary)] text-white' : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]'}`}>
              {t(`deposits.${tab.label}`)} <span className="opacity-70">({counts[tab.key]})</span>
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
          <input type="text" placeholder={t('deposits.searchByIdNameEmail')} value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-sm focus:border-[var(--accent-primary)] focus:outline-none text-[var(--text-primary)] placeholder-[var(--text-tertiary)]" />
        </div>
      </div>

      <div className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-[var(--text-secondary)]">
            <AlertCircle className="w-8 h-8" />
            <p className="text-sm">{t('deposits.noDepositsFound')}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-[2rem_1fr_1.2fr_1fr_1fr_1rem] items-center gap-3 px-4 py-2.5 bg-[var(--bg-secondary)] border-b border-[var(--border-color)] text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
              <span /><span>{t('deposits.trader')}</span><span>{t('deposits.method')}</span><span>{t('deposits.amount')}</span><span>{t('common.status')}</span><span />
            </div>
            {filtered.map(d => {
              const isOpen   = expanded === d.id;
              const isAction = actionId === d.id;
              const canAct   = d.status === 'proof_uploaded';
              return (
                <div key={d.id} className="border-b border-[var(--border-color)] last:border-0">
                  <button type="button" onClick={() => toggleExpanded(d.id)}
                    className="w-full grid grid-cols-[2rem_1fr_1.2fr_1fr_1fr_1rem] items-center gap-3 px-4 py-3 text-left hover:bg-[var(--bg-secondary)] transition-smooth">
                    <span className="text-[var(--text-tertiary)]">
                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </span>
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]">
                        <User className="w-3.5 h-3.5" />
                      </span>
                      <span className="min-w-0">
                        <p className="text-sm font-medium text-[var(--text-primary)] truncate">{d.trader_name || '—'}</p>
                        <p className="text-xs text-[var(--text-secondary)] truncate">{d.trader_email || d.account_id}</p>
                      </span>
                    </span>
                    <span className="text-sm text-[var(--text-primary)]">
                      <span className="font-medium capitalize">{d.pluginKey?.replace('_', ' ')}</span>
                      {d.method_details?.bankName && <span className="text-xs text-[var(--text-tertiary)] ml-1.5">· {d.method_details.bankName}</span>}
                      {d.method_details?.coin && <span className="text-xs text-[var(--text-tertiary)] ml-1.5">· {d.method_details.coin} / {d.method_details.network}</span>}
                    </span>
                    <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                      +{Number(d.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      <span className="ml-1 text-xs font-normal text-[var(--text-secondary)]">{d.currency}</span>
                    </span>
                    <span><StatusBadge status={d.status} t={t} /></span>
                    <span />
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 border-t border-[var(--border-color)] bg-[var(--bg-secondary)]">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-3">
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">{t('common.details')}</p>
                          <div className="text-xs space-y-1 text-[var(--text-primary)]">
                            <p><span className="text-[var(--text-tertiary)]">ID:</span> <span className="font-mono">{d.id}</span></p>
                            <p><span className="text-[var(--text-tertiary)]">{t('common.createdAt')}:</span> {new Date(d.created_at).toLocaleString()}</p>
                            <p><span className="text-[var(--text-tertiary)]">{t('deposits.depositProof')}:</span> {d.proof_uploaded_at ? new Date(d.proof_uploaded_at).toLocaleString() : '—'}</p>
                            {d.rejection_note && <p className="mt-1 italic text-red-600 dark:text-red-400">{t('common.description')}: {d.rejection_note}</p>}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">{t('deposits.method')}</p>
                          <div className="text-xs space-y-1 text-[var(--text-primary)]">
                            {d.method_details ? Object.entries(d.method_details).map(([k, v]) => (
                              <p key={k}><span className="text-[var(--text-tertiary)] capitalize">{k.replace(/([A-Z])/g, ' $1').trim()}:</span>{' '}<span className="font-mono break-all">{String(v)}</span></p>
                            )) : <p className="text-[var(--text-tertiary)]">—</p>}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">{t('deposits.depositProof')}</p>
                          {d.proof_file_path ? (
                            <div className="space-y-2">
                              {isImage(d.proof_file_path) && (
                                <button onClick={() => openProof(d.proof_file_path)} className="block w-full">
                                  <img
                                    src={getProofURL(d.proof_file_path)}
                                    alt={t('deposits.proofThumbnail')}
                                    className="w-full max-h-28 object-cover rounded-lg border hover:opacity-80 transition-opacity"
                                    style={{ borderColor: 'var(--border-color)' }}
                                  />
                                </button>
                              )}
                              <ProofButton filePath={d.proof_file_path} onOpen={openProof} t={t} />
                            </div>
                          ) : (
                            <p className="text-xs text-[var(--text-tertiary)]">{t('common.noData')}</p>
                          )}
                        </div>
                      </div>

                      {canAct && !isAction && (
                        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[var(--border-color)]">
                          <button onClick={() => openAction(d.id, 'approve')} className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-primary-dark)] text-xs flex items-center gap-1 transition-smooth">
                            <CheckCircle className="w-3.5 h-3.5" /> {t('deposits.approve')}
                          </button>
                          <button onClick={() => openAction(d.id, 'reject')} className="px-4 py-2 border border-red-300 dark:border-red-700 rounded-lg text-xs flex items-center gap-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-smooth">
                            <XCircle className="w-3.5 h-3.5" /> {t('deposits.reject')}
                          </button>
                        </div>
                      )}

                      {isAction && (
                        <div className={`mt-4 pt-3 border-t ${actionType === 'approve' ? 'border-green-200 dark:border-green-800' : 'border-red-200 dark:border-red-800'}`}>
                          <p className={`text-xs font-medium mb-2 ${actionType === 'approve' ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                            {actionType === 'approve' ? t('deposits.confirmApproval') : t('deposits.confirmRejection')}
                          </p>
                          {actionType === 'reject' && (
                            <textarea value={note} onChange={e => setNote(e.target.value)}
                              placeholder={t('deposits.rejectionReason')} rows={2}
                              className="w-full px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-xs mb-2 focus:border-[var(--accent-primary)] focus:outline-none text-[var(--text-primary)] placeholder-[var(--text-tertiary)]" />
                          )}
                          <div className="flex items-center gap-2">
                            <button onClick={submitAction} disabled={submitting}
                              className={`px-4 py-2 rounded-lg text-xs flex items-center gap-1 disabled:opacity-50 transition-smooth ${actionType === 'approve' ? 'bg-[var(--accent-primary)] hover:bg-[var(--accent-primary-dark)] text-white' : 'bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white'}`}>
                              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (actionType === 'approve' ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />)}
                              {submitting ? t('common.processing') : (actionType === 'approve' ? t('deposits.approve') : t('deposits.reject'))}
                            </button>
                            <button onClick={cancelAction} className="px-4 py-2 border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-secondary)] text-xs text-[var(--text-primary)] transition-smooth">{t('common.cancel')}</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}